import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {Spinner} from '@dropbox/dig-components/progress_indicators';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ArrowLeftLine, ChevronRightLine, LineViewLine} from '@dropbox/dig-icons/assets';
import {AiDocumentMini, MegaphoneMini} from '@dropbox/dig-illustrations';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {growthbookCacheAtom} from 'atoms/growthbook';
import type {CoachSubmissionContext} from 'client';
import {Workstream} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {UpdatePost} from 'components/DSYS/UpdatePost';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {type ClientGuide, EXTERNAL_CLIENT_GUIDES} from 'pages/mcpClientGuides';
import {ComponentType, SVGProps, useCallback, useEffect, useState} from 'react';

import {useAllUpdates} from './hooks';
import {useWorkstreamAutodraft} from './useWorkstreamAutodraft';
import {useWorkstreamMcpConnections} from './useWorkstreamMcpConnections';
import {WorkstreamAskChat} from './WorkstreamAskChat';
import type {ChatDraftHistoryControl} from './workstreamChatDraftHistory';
import {WorkstreamMcpAppsIntro} from './WorkstreamMcpAppsIntro';
import {McpConnectedView} from './workstreamMcpSteps';
import {WorkstreamStatusChat} from './WorkstreamStatusChat';

export type {ChatDraftHistoryControl} from './workstreamChatDraftHistory';

const UpdatesHeader = ({
  icon,
  label,
  isExpanded,
  onClick,
}: {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  label: string;
  isExpanded: boolean;
  onClick: () => void;
}) => (
  <Box
    width="100%"
    padding="12"
    borderTop="Solid"
    borderColor="Border Subtle"
    borderWidth="1"
    cursor="pointer"
    onClick={onClick}
  >
    <Box display="flex" alignItems="center" justifyContent="space-between" style={{gap: 8}}>
      <Box display="flex" alignItems="center" style={{gap: 8, minWidth: 0, flex: 1}}>
        <UIIcon src={icon} className={atoms({flexShrink: 0})} />
        <Text isBold className={atoms({flexShrink: 0})}>
          {label}
        </Text>
      </Box>
      <IconButton
        variant="borderless"
        size="small"
        aria-label={isExpanded ? t('collapse') : t('expand')}
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
      >
        <UIIcon
          src={ChevronRightLine}
          className={atoms({color: 'Text Subtle'})}
          style={{
            transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        />
      </IconButton>
    </Box>
  </Box>
);

export const WorkstreamUpdates = ({workstream}: {workstream: Workstream}) => {
  const {data: allUpdates, isLoading: isLoadingAllUpdates} = useAllUpdates(workstream.id);

  const updates = isLoadingAllUpdates ? workstream.updates?.slice(0, 2) : allUpdates;

  return (
    <Stack>
      {updates?.length ? (
        <>
          {updates?.map((update) => (
            <UpdatePost key={update.id} update={update} employee={update.author} includeArchived>
              <RichTextArea value={update.comment} />
            </UpdatePost>
          ))}
          {isLoadingAllUpdates && <Spinner />}
        </>
      ) : (
        <EmptyState
          hideBorder
          title={t('no_updates_yet')}
          body={t('no_updates_yet_body')}
          image={<MegaphoneMini width="64" altText={t('no_updates_yet')} />}
        />
      )}
    </Stack>
  );
};

type AssistantChatProps = {
  workstream: Workstream;
  editorValue?: string;
  selectedStatus?: string;
  onCoachContextChange?: (ctx: CoachSubmissionContext | null) => void;
  onApplySuggestionToDraft?: (markdown: string, status?: string) => void;
  onChatPersistenceFingerprintChange?: (fingerprint: string) => void;
  onAgentInteractionChange?: (used: boolean) => void;
  chatDraftHistoryControl?: ChatDraftHistoryControl;
};

const AutodraftAssistant = (props: AssistantChatProps) => {
  const {isAskWorkstreamUpdateEnabled} = useAtomValue(growthbookCacheAtom);
  const {data: autodraft, isLoading} = useWorkstreamAutodraft(props.workstream.id);
  const {connectClient} = useWorkstreamMcpConnections();
  const [view, setView] = useState<'chat' | 'mcp' | null>(null);
  const [selectedMcpGuide, setSelectedMcpGuide] = useState<ClientGuide | null>(null);
  const [autoGenerateSignal, setAutoGenerateSignal] = useState(0);
  const [showDraftCta, setShowDraftCta] = useState(true);

  const {onAgentInteractionChange} = props;

  const handleDraftUpdate = useCallback(() => {
    setAutoGenerateSignal((n) => n + 1);
    setShowDraftCta(false);
    onAgentInteractionChange?.(true);
    analyticsLogger().logEvent('AUTODRAFT_DRAFT_GENERATED', {
      workstream_id: String(props.workstream.id),
    });
  }, [props.workstream.id, onAgentInteractionChange]);
  const handleAgentInteraction = useCallback(
    (used: boolean) => {
      if (used) {
        setShowDraftCta(false);
      }
      onAgentInteractionChange?.(used);
    },
    [onAgentInteractionChange]
  );

  const handleMcpConnect = useCallback(
    (clientId: string) => {
      connectClient(clientId);
      const guide = EXTERNAL_CLIENT_GUIDES.find((g) => g.id === clientId) ?? null;
      setSelectedMcpGuide(guide);
      setView('mcp');
      analyticsLogger().logEvent('AUTODRAFT_SETUP_COMPLETED', {
        mode: 'mcp',
        client_id: clientId,
      });
    },
    [connectClient]
  );

  const [composerHasInput, setComposerHasInput] = useState(false);
  const handleHasInputChange = useCallback((hasInput: boolean) => {
    setComposerHasInput(hasInput);
  }, []);

  useEffect(() => {
    if (view === null && autodraft) {
      setView('chat');
    }
  }, [autodraft, view]);

  if (isLoading || view === null) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{flex: '1 1 0%', minHeight: 0}}
      >
        <Spinner />
      </Box>
    );
  }

  if (view === 'mcp' && selectedMcpGuide) {
    return (
      <Box display="flex" flexDirection="column" style={{flex: '1 1 0%', minHeight: 0}}>
        <Box paddingX="16" paddingY="8" style={{flexShrink: 0}}>
          <Button
            variant="borderless"
            size="small"
            withIconStart={<UIIcon src={ArrowLeftLine} />}
            onClick={() => setView('chat')}
          >
            {t('back')}
          </Button>
        </Box>
        <McpConnectedView
          workstream={props.workstream}
          connectedClientIds={[selectedMcpGuide.id]}
        />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" style={{flex: '1 1 0%', minHeight: 0}}>
      {isAskWorkstreamUpdateEnabled ? (
        <WorkstreamAskChat
          key={`ask-gen-${autoGenerateSignal}`}
          workstream={props.workstream}
          draftText={props.editorValue}
          selectedStatus={props.selectedStatus}
          onApplySuggestionToDraft={props.onApplySuggestionToDraft}
          onChatPersistenceFingerprintChange={props.onChatPersistenceFingerprintChange}
          onAgentInteractionChange={handleAgentInteraction}
          onDraftUpdate={handleDraftUpdate}
          autoGenerateSignal={autoGenerateSignal}
          chatDraftHistoryControl={props.chatDraftHistoryControl}
          onMcpConnect={handleMcpConnect}
        />
      ) : (
        <WorkstreamStatusChat
          key={`autodraft-gen-${autoGenerateSignal}`}
          workstream={props.workstream}
          draftText={props.editorValue}
          selectedStatus={props.selectedStatus}
          onCoachContextChange={props.onCoachContextChange}
          onApplySuggestionToDraft={props.onApplySuggestionToDraft}
          onChatPersistenceFingerprintChange={props.onChatPersistenceFingerprintChange}
          onAgentInteractionChange={handleAgentInteraction}
          onHasInputChange={handleHasInputChange}
          chatDraftHistoryControl={props.chatDraftHistoryControl}
          autoGenerateSignal={autoGenerateSignal}
          autoGeneratePrompt={t('autodraft_generate_prompt')}
          deferDraftApply={false}
          disableAssist
          scrollHeaderContent={
            showDraftCta && !composerHasInput ? (
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                minHeight="100%"
                paddingX="16"
                paddingY="8"
              >
                <WorkstreamMcpAppsIntro onMcpConnect={handleMcpConnect} />
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  style={{flex: '1 1 0%'}}
                >
                  <EmptyState
                    hideBorder
                    paddingY="0"
                    marginBottom="0"
                    image={<AiDocumentMini width="64" altText="" />}
                    title={t('autodraft_ready_title')}
                    body={t('autodraft_ready_body')}
                    cta={{text: t('autodraft_ready_generate'), onClick: handleDraftUpdate}}
                  />
                </Box>
              </Box>
            ) : undefined
          }
        />
      )}
    </Box>
  );
};

const UPDATES_HEADER_HEIGHT_PX = 48;

export const WorkstreamUpdateTabs = ({
  workstream,
  editorValue,
  selectedStatus,
  onCoachContextChange,
  onApplySuggestionToDraft,
  onChatPersistenceFingerprintChange,
  onAgentInteractionChange,
  chatDraftHistoryControl,
}: {
  workstream: Workstream;
  editorValue?: string;
  selectedStatus?: string;
  onCoachContextChange?: (ctx: CoachSubmissionContext | null) => void;
  onApplySuggestionToDraft?: (markdown: string, status?: string) => void;
  onChatPersistenceFingerprintChange?: (fingerprint: string) => void;
  onAgentInteractionChange?: (used: boolean) => void;
  chatDraftHistoryControl?: ChatDraftHistoryControl;
}) => {
  const [updatesExpanded, setUpdatesExpanded] = useState(false);

  const toggleUpdates = useCallback(() => {
    setUpdatesExpanded((prev) => {
      const next = !prev;
      analyticsLogger().logEvent('WORKSTREAM_STATUS_TAB_SELECTED', {
        tab: next ? 'updates' : 'assistant',
      });
      return next;
    });
  }, []);

  return (
    <Box
      display="flex"
      flexDirection="column"
      style={{flex: 1, height: '100%', minHeight: 0, minWidth: 0, position: 'relative'}}
      overflowY="hidden"
    >
      <Box display="flex" flexDirection="column" style={{flex: '1 1 0%', minHeight: 0}}>
        <AutodraftAssistant
          workstream={workstream}
          editorValue={editorValue}
          selectedStatus={selectedStatus}
          onCoachContextChange={onCoachContextChange}
          onApplySuggestionToDraft={onApplySuggestionToDraft}
          onChatPersistenceFingerprintChange={onChatPersistenceFingerprintChange}
          onAgentInteractionChange={onAgentInteractionChange}
          chatDraftHistoryControl={chatDraftHistoryControl}
        />
      </Box>

      {updatesExpanded && (
        <Box
          position="absolute"
          backgroundColor="Background Base"
          overflowY="auto"
          style={{
            top: 0,
            left: 0,
            right: 0,
            bottom: UPDATES_HEADER_HEIGHT_PX,
            zIndex: 1,
          }}
        >
          <WorkstreamUpdates workstream={workstream} />
        </Box>
      )}

      <Box style={{flexShrink: 0}}>
        <UpdatesHeader
          icon={LineViewLine}
          label={t('updates')}
          isExpanded={updatesExpanded}
          onClick={toggleUpdates}
        />
      </Box>
    </Box>
  );
};
